import React, { useState } from "react";
import { Plus, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useEa } from "@/components/energy-analytics/analytics/EaContext";
import { matchPoint } from "@/components/energy-analytics/analytics/EaControls";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/lib/auth";
import { EaSeg, inputCls as inputClsSafe } from "@/components/energy-analytics/analytics/EaUi";
import { SCOPES, SCOPE_LABEL, gasLabel } from "@/components/energy-analytics/analytics/eaFormat";

const empty = { scope: "site", name: "", address: "", codes: [], drivers: [{ name: "Produzione", unit: "t" }] };
const ptName = (p) => (p.commodity === "GAS" ? gasLabel(p) : `POD ${p.identifier}`);

function SiteForm({ site, hasCompany, onSaved, onCancel }) {
  const { nav, filters } = useEa();
  const [f, setF] = useState(site ? { ...site, drivers: site.drivers.map((d) => ({ ...d })) } : empty);
  const scopes = SCOPES.filter(([k]) => k !== "company" || !hasCompany || site?.scope === "company");
  const toggle = (code) => set("codes", f.scope === "point" ? [code] : f.codes.includes(code) ? f.codes.filter((c) => c !== code) : [...f.codes, code]);
  const [qq, setQ] = useState("");
  const points = (nav?.points || []).filter((p) => matchPoint(p, qq));
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const setDrv = (i, k, v) => set("drivers", f.drivers.map((d, j) => (j === i ? { ...d, [k]: v } : d)));
  const save = async () => {
    const pt = (nav?.points || []).find((p) => p.code === f.codes[0]);
    const name = f.name.trim() || (f.scope === "company" ? "Totale aziendale" : f.scope === "point" && pt ? ptName(pt) : "");
    if (!name || (f.scope !== "company" && !f.codes.length)) return toast.error(f.scope === "point" ? "Seleziona il POD/PDR" : "Indica il nome del sito e almeno un punto di fornitura");
    const body = { scope: f.scope, name, address: f.address || null, codes: f.codes, drivers: f.drivers.filter((d) => d.name.trim() && d.unit.trim()) };
    const params = { company_id: filters.company || undefined };
    try {
      await (site ? api.put(`/ea/efficiency/sites/${site.id}`, body, { params }) : api.post("/ea/efficiency/sites", body, { params }));
      toast.success("Ambito salvato");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Salvataggio non riuscito");
    }
  };
  return (
    <div className="card-elevated p-5 space-y-4" data-testid="eff-site-form">
      {!site && <EaSeg value={f.scope} onChange={(v) => setF({ ...f, scope: v, codes: [] })} options={scopes} testid="eff-site-scope" />}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <input value={f.name} onChange={(e) => set("name", e.target.value)} placeholder={f.scope === "company" ? "Totale aziendale" : f.scope === "point" ? "Nome (facoltativo, es. Cella frigo)" : "Nome sito (es. Stabilimento Molteno)"} className={inputClsSafe} data-testid="eff-site-name" />
        <input value={f.address || ""} onChange={(e) => set("address", e.target.value)} placeholder="Indirizzo" className={inputClsSafe} data-testid="eff-site-address" />
      </div>
      {f.scope === "company" ? <div className="text-sm text-slate-500" data-testid="eff-site-company-note">Comprende automaticamente tutti i punti di fornitura dell'azienda.</div> : <div>
        <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">{f.scope === "point" ? "Punto di fornitura" : `Punti di fornitura del sito (${f.codes.length})`}</div>
        <input value={qq} onChange={(e) => setQ(e.target.value)} placeholder="Cerca POD/PDR, via o città…" className={`${inputClsSafe} w-full mb-2`} data-testid="eff-site-point-search" />
        <div className="max-h-48 overflow-y-auto grid grid-cols-1 md:grid-cols-2 gap-1">
          {points.map((p) => (
            <label key={p.code} className="flex items-start gap-2 rounded px-2 py-1 hover:bg-slate-50 cursor-pointer text-sm" data-testid={`eff-site-point-${p.identifier}`}>
              <Checkbox checked={f.codes.includes(p.code)} onCheckedChange={() => toggle(p.code)} className="mt-0.5" />
              <span><b>{ptName(p)}</b><span className="block text-xs text-slate-500">{[p.label, p.address].filter(Boolean).join(" · ")}</span></span>
            </label>
          ))}
        </div>
      </div>}
      <div>
        <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Driver (es. tonnellate prodotte, ore lavorate, m², dipendenti)</div>
        {f.drivers.map((d, i) => (
          <div key={d.id || i} className="flex gap-2 mb-2">
            <input value={d.name} onChange={(e) => setDrv(i, "name", e.target.value)} placeholder="Nome driver" className={`${inputClsSafe} flex-1`} data-testid={`eff-driver-name-${i}`} />
            <input value={d.unit} onChange={(e) => setDrv(i, "unit", e.target.value)} placeholder="Unità" className={`${inputClsSafe} w-28`} data-testid={`eff-driver-unit-${i}`} />
            <button onClick={() => set("drivers", f.drivers.filter((_, j) => j !== i))} className="text-slate-400 hover:text-red-600" data-testid={`eff-driver-remove-${i}`}><Trash2 size={16} /></button>
          </div>
        ))}
        <button onClick={() => set("drivers", [...f.drivers, { name: "", unit: "" }])} className="text-xs font-semibold text-blue-600 inline-flex items-center gap-1" data-testid="eff-driver-add"><Plus size={14} /> Aggiungi driver</button>
        {site && <div className="text-xs text-slate-400 mt-1">Rimuovendo un driver si cancellano anche i suoi valori mensili.</div>}
      </div>
      <div className="flex gap-2">
        <button onClick={save} className="h-9 px-4 rounded-lg bg-slate-900 text-white text-sm font-semibold inline-flex items-center gap-2" data-testid="eff-site-save"><Save size={15} /> Salva</button>
        <button onClick={onCancel} className="h-9 px-4 rounded-lg border border-slate-200 text-sm" data-testid="eff-site-cancel">Annulla</button>
      </div>
    </div>
  );
}

function Factors({ factors, onSaved }) {
  const { user } = useAuth();
  const [f, setF] = useState(factors);
  const staff = ["admin", "editor"].includes(user?.role);
  const rows = [["tep_per_mwh", "TEP per MWh elettrico"], ["tep_per_ksmc", "TEP per 1.000 Smc gas"]];
  const save = async () => {
    try { await api.put("/ea/efficiency/factors", Object.fromEntries(rows.map(([k]) => [k, Number(f[k])]))); toast.success("Fattori aggiornati"); onSaved(); } catch { toast.error("Valori non validi"); }
  };
  return (
    <div className="card-elevated p-5" data-testid="eff-factors">
      <div className="font-display font-semibold text-slate-900 mb-1">Fattori di conversione TEP</div>
      <div className="text-xs text-slate-500 mb-3">Valori standard ARERA. {staff ? "Come Adybo puoi modificarli: valgono per tutti i clienti." : "Sono gestiti da Adybo."} I fattori emissivi CO₂ per anno si gestiscono nella sezione Sostenibilità.</div>
      <div className="grid grid-cols-2 gap-3 max-w-xl">
        {rows.map(([k, l]) => (
          <label key={k} className="text-xs text-slate-600">{l}
            <input type="number" step="0.001" value={f[k]} disabled={!staff} onChange={(e) => setF({ ...f, [k]: e.target.value })} className={`${inputClsSafe} w-full mt-1`} data-testid={`eff-factor-${k}`} />
          </label>
        ))}
      </div>
      {staff && <button onClick={save} className="mt-3 h-9 px-4 rounded-lg bg-slate-900 text-white text-sm font-semibold" data-testid="eff-factors-save">Salva fattori</button>}
    </div>
  );
}

export function EffSites({ data, reload }) {
  const { filters } = useEa();
  const [edit, setEdit] = useState(null);
  const del = async (s) => {
    if (!window.confirm(`Eliminare "${s.name}" e tutti i valori dei suoi driver?`)) return;
    await api.delete(`/ea/efficiency/sites/${s.id}`, { params: { company_id: filters.company || undefined } });
    toast.success("Ambito eliminato");
    reload();
  };
  const done = () => { setEdit(null); reload(); };
  const hasCompany = data.sites.some((s) => s.scope === "company");
  return (
    <div className="space-y-4" data-testid="eff-sites">
      {edit ? <SiteForm site={edit === "new" ? null : edit} hasCompany={hasCompany} onSaved={done} onCancel={() => setEdit(null)} /> : (
        <button onClick={() => setEdit("new")} className="h-9 px-4 rounded-lg bg-slate-900 text-white text-sm font-semibold inline-flex items-center gap-2" data-testid="eff-site-new"><Plus size={15} /> Nuovo ambito</button>
      )}
      {data.sites.map((s) => (
        <div key={s.id} className="card-elevated p-4 flex items-start justify-between gap-3" data-testid={`eff-site-${s.id}`}>
          <div>
            <div className="font-semibold text-slate-900">{s.name} <span className="ml-1 text-[10px] uppercase tracking-wider font-semibold rounded bg-emerald-50 text-emerald-700 px-1.5 py-0.5" data-testid={`eff-site-scope-${s.id}`}>{SCOPE_LABEL[s.scope]}</span></div>
            <div className="text-xs text-slate-500">{s.address || "—"} · {s.scope === "company" ? "tutti i punti" : s.scope === "point" ? "singolo punto" : `${s.codes.length} ${s.codes.length === 1 ? "punto" : "punti"}`} · driver: {s.drivers.map((d) => `${d.name} (${d.unit})`).join(", ") || "nessuno"}</div>
          </div>
          <div className="flex gap-2">
            <button onClick={() => setEdit(s)} className="text-xs font-semibold text-blue-600" data-testid={`eff-site-edit-${s.id}`}>Modifica</button>
            <button onClick={() => del(s)} className="text-xs font-semibold text-red-600" data-testid={`eff-site-delete-${s.id}`}>Elimina</button>
          </div>
        </div>
      ))}
      <Factors factors={data.factors} onSaved={reload} />
    </div>
  );
}
